import React, { useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  Checkbox,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatLastModified } from '../../utilities/dateFormat';
import { Design } from './DesignsList';
import { uploadThumbnail, deleteDesigns, copySingleDesign, exportDesignsAsPDF, exportAsWorkplaceDesigns } from '../../services/designsService';
import { useToast } from '../../contexts/ToastContext';
import DesignNameDialog from '../organisms/DesignNameDialog';
import ExportDialog from '../organisms/ExportDialog';
import LoadingOverlay from '../atoms/LoadingOverlay';

interface DesignsThumbnailProps {
  designs: Design[];
  allSelected: boolean;
  someSelected: boolean;
  handleSelectAll: (checked: boolean) => void;
  handleSelectDesign: (uniqueKey: string, checked: boolean) => void;
  handleEditDesignByClick: (design: Design) => void;
  organizationId: string;
  onDesignUpdate?: (updatedDesign: Design) => void;
  onDesignDelete?: (designId: string) => void;
  onDesignDuplicate?: () => void;
  getLocationName?: (locationId: string) => string;
}

const DefaultThumbnailImage = '/images/DefaultThumbnailDesign.png'; // Default image path

// Transform Azure Storage URLs to use proxy to avoid CORS issues
const transformThumbnailUrl = (url: string | null | undefined): string => {
  if (!url) return DefaultThumbnailImage;
  
  // Check if it's a direct Azure Storage URL
  if (url.includes('localhost:10000/devstoreaccount1')) {
    // Transform to use proxy: http://localhost:10000/devstoreaccount1/... -> /api/storage/devstoreaccount1/...
    return url.replace('http://localhost:10000', '/api/storage');
  }
  
  return url;
};

const DesignsThumbnail: React.FC<DesignsThumbnailProps> = ({
  designs,
  allSelected,
  someSelected,
  handleSelectAll,
  handleSelectDesign,
  handleEditDesignByClick,
  organizationId,
  onDesignUpdate,
  onDesignDelete,
  onDesignDuplicate,
  getLocationName,
}) => {
  const theme = useTheme();
  const { showConfirm, showError, showSuccess } = useToast();
  const [uploadingDesigns, setUploadingDesigns] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const handleThumbnailUpload = async (file: File, designId: string): Promise<string> => {
    try {
      setUploadingDesigns(prev => new Set(prev).add(designId));

      // Get the current design for later update
      const currentDesign = designs.find(d => d.id === designId);

      // Upload the thumbnail
      const thumbnailUrl = await uploadThumbnail(file, designId, organizationId);

      // Update the design with the new thumbnail URL
      if (currentDesign && onDesignUpdate) {
        // Add cache-busting parameter to force browser to reload the image
        const cacheBustingUrl = thumbnailUrl + (thumbnailUrl.includes('?') ? '&' : '?') + `t=${Date.now()}`;
        
        const updatedDesign = {
          ...currentDesign,
          thumbnailUrl: cacheBustingUrl
        };
        onDesignUpdate(updatedDesign);
      }
      
      return thumbnailUrl;
    } catch (error) {
      console.error('Error uploading thumbnail:', error);
      throw error;
    } finally {
      setUploadingDesigns(prev => {
        const newSet = new Set(prev);
        newSet.delete(designId);
        return newSet;
      });
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, designId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedDesignId(designId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    if (selectedDesignId) {
      const designToEdit = designs.find(d => d.id === selectedDesignId);
      if (designToEdit) {
        handleEditDesignByClick(designToEdit);
      }
    }
    handleMenuClose();
  };

  const handleUploadClick = () => {
    // Create a file input element and trigger click
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file && selectedDesignId) {
        try {
          await handleThumbnailUpload(file, selectedDesignId);
          showSuccess('Thumbnail uploaded successfully');
        } catch (error) {
          console.error('Error uploading thumbnail:', error);
          // Show user-friendly error message based on the error type
          const errorMessage = error instanceof Error
            ? error.message
            : 'Failed to upload thumbnail. Please try again.';
          showError(errorMessage);
        }
      }
    };
    fileInput.click();
    handleMenuClose();
  };

  const handleDuplicateClick = () => {
    if (selectedDesignId) {
      const designToDuplicate = designs.find(d => d.id === selectedDesignId);
      if (designToDuplicate) {
        // Open the duplicate dialog
        setDuplicateDialogOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleDuplicateConfirm = async (data: { name: string; type?: string; workplaceLocation?: string }) => {
    // Close the dialog first
    setDuplicateDialogOpen(false);

    if (!selectedDesignId) {
      showError('No design selected for duplication.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      // Use the new copySingleDesign mutation that accepts a name
      const copyResponse = await copySingleDesign({
        designId: selectedDesignId,
        newName: data.name,
        targetOrganizationId: organizationId,
        locationId: data.workplaceLocation
      });

      // Check if the copy was successful
      if (copyResponse.success && copyResponse.newId) {
        showSuccess(`Design "${data.name}" created successfully!`);

        // Call the refresh callback if provided
        if (onDesignDuplicate) {
          onDesignDuplicate();
        }

      } else {
        throw new Error(copyResponse.message || 'Failed to create copy of the design');
      }
    } catch (error) {
      console.error('Error duplicating design:', error);
      showError('Failed to duplicate design. Please try again.');
    }
  };

  const handleExportClick = () => {
    if (selectedDesignId) {
      const designToExport = designs.find(d => d.id === selectedDesignId);
      if (designToExport) {
        setExportDialogOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleExportPDF = async () => {
    if (!selectedDesignId) {
      showError('No design selected for export.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      const blob = await exportDesignsAsPDF([selectedDesignId], organizationId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `design-export-${selectedDesignId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting design as PDF:', error);
      showError('Failed to export PDF. Please try again.');
    }
  };

  const handleExportWorkplace = async () => {
    if (!selectedDesignId) {
      showError('No design selected for export.');
      return;
    }

    if (!organizationId) {
      showError('Organization not found. Please try refreshing the page.');
      return;
    }

    try {
      await exportAsWorkplaceDesigns([selectedDesignId], () => {
        showSuccess('Design exported successfully!');
      });
    } catch (error) {
      console.error('Error exporting workplace design:', error);
      showError('Failed to export design. Please try again.');
    }
  };

  const handleDeleteClick = async () => {
    if (selectedDesignId && onDesignDelete) {
      // Find the design to be deleted to get its name
      const designToDelete = designs.find(d => d.id === selectedDesignId);
      const designName = designToDelete?.project || 'this design';

      // Show confirmation dialog using the same styled dialog as the bottom panel
      const confirmed = await showConfirm({
        title: 'Delete Design',
        message: 'Are you sure you want to delete this design? This is a permanent action and can not be undone.',
        type: 'warning',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        showIcon: false
      });

      if (confirmed) {
        try {
          await deleteDesigns([selectedDesignId]);
          onDesignDelete(selectedDesignId);

          // Show success toast
          const successMessage = `Design "${designName}" deleted successfully`;
          showSuccess(successMessage);
        } catch (error) {
          console.error('Error deleting design:', error);
          showError('Failed to delete design. Please try again.');
        }
      }
    }
    handleMenuClose();
  };


  return (
    <ThumbnailContainer>
      {designs.length === 0 ? (
        <Typography sx={{ 
          color: theme.palette.text.secondary, 
          fontSize: '14px', 
          fontStyle: 'italic', 
          textAlign: 'center', 
          width: '100%' 
        }}>
          No designs found
        </Typography>
      ) : (
        <>
          <SelectAllContainer>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              sx={{ 
                color: theme.palette.action.disabled,
                '&.Mui-checked': { 
                  color: theme.palette.primary.main 
                },
                '&.MuiCheckbox-indeterminate': {
                  color: theme.palette.primary.main
                }
              }}
            />
            <Typography sx={{ 
              fontSize: '14px', 
              color: theme.palette.text.primary, 
              fontWeight: 600 
            }}>
              Select All
            </Typography>
          </SelectAllContainer>
          {designs.map((design) => (
            <ThumbnailCard key={design.uniqueKey} className="thumbnail-card">
              <ThumbnailHeader backgroundImage={transformThumbnailUrl(design.thumbnailUrl)}>
                <Checkbox
                  checked={design.selected}
                  onChange={(e) => {
                    e.stopPropagation(); // Prevent card click when checkbox is clicked
                    handleSelectDesign(design.uniqueKey, e.target.checked);
                  }}
                  className="thumbnail-checkbox"
                  sx={{
                    padding: '1px',
                    color: theme.palette.text.secondary,
                    backgroundColor: 'transparent',
                    borderRadius: '4px',
                    opacity: design.selected ? 1 : 0,
                    transition: 'opacity 0.2s ease-in-out',
                    '.thumbnail-card:hover &': {
                      opacity: 1
                    },
                    '&.Mui-checked': {
                      color: theme.palette.primary.main,
                      backgroundColor: 'transparent',
                      opacity: 1
                    },
                    '&:hover': {
                      backgroundColor: 'transparent',
                      opacity: 1
                    },
                    '&.Mui-checked:hover': {
                      backgroundColor: 'transparent',
                      opacity: 1
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.5rem',
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: '1px'
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                      backgroundColor: 'transparent'
                    }
                  }}
                />
                {/* Loading overlay for thumbnail upload */}
                {uploadingDesigns.has(design.id) && (
                  <LoadingOverlay label="Uploading..." size={24} />
                )}
              </ThumbnailHeader>
              <ThumbnailDetails>
                <Tooltip
                  title={design.project}
                  placement="top"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                        fontFamily: 'Open Sans'
                      }
                    }
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: 'Open Sans',
                      fontSize: '16px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '150%', /* 24px */
                      letterSpacing: '-0.32px',
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: theme.palette.primary.main
                      }
                    }}
                    onClick={() => handleEditDesignByClick(design)}
                  >
                    {design.project}
                  </Typography>
                </Tooltip>
                {(() => {
                  // Always render a line for location, even if empty
                  if (!getLocationName || !design.locationId) {
                    return (
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Open Sans',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '150%', /* 21px */
                          letterSpacing: '-0.28px',
                          color: theme.palette.text.secondary
                        }}
                      >
                        No location selected
                      </Typography>
                    );
                  }

                  const fullLocation = getLocationName(design.locationId);

                  return (
                    <Tooltip
                      title={fullLocation}
                      placement="top"
                      arrow
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                            fontFamily: 'Open Sans'
                          }
                        }
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'Open Sans',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '150%', /* 21px */
                          letterSpacing: '-0.28px',
                          color: theme.palette.text.secondary,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {fullLocation}
                      </Typography>
                    </Tooltip>
                  );
                })()}
                <Typography variant="body2" sx={{
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%', /* 21px */
                  letterSpacing: '-0.28px',
                  color: theme.palette.text.secondary
                }}>
                  {design.designer}
                </Typography>
                <Typography variant="body2" sx={{
                  fontFamily: 'Open Sans',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '150%', /* 21px */
                  letterSpacing: '-0.28px',
                  color: theme.palette.text.secondary
                }}>
                  {formatLastModified(design.updatedAt)}
                </Typography>
              </ThumbnailDetails>
              <HamburgerMenuButton>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, design.id)}
                  sx={{
                    backgroundColor: 'transparent',
                    color: theme.palette.text.primary,
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </HamburgerMenuButton>
            </ThumbnailCard>
          ))}
          
          {/* Context menu */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                fontFamily: 'Open Sans',
                fontSize: '12px',
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                backgroundImage: 'none',
              },
              '& .MuiMenuItem-root': {
                fontFamily: 'Open Sans',
                fontSize: '12px',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              },
              '& .MuiListItemText-primary': {
                fontFamily: 'Open Sans',
                fontSize: '12px',
                color: theme.palette.text.primary,
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.text.secondary,
                minWidth: 'unset !important',
                marginRight: '6px !important'
              },
              '& .MuiDivider-root': {
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.25)'
                  : 'rgba(17, 17, 17, 0.4)'
              },
            }}
          >
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <Box
                  component="img"
                  src="/images/edit-light.svg"
                  alt="Edit"
                  sx={{
                    width: 18,
                    height: 18,
                    filter: theme.palette.mode === 'dark'
                      ? 'brightness(0) invert(1)'
                      : 'none'
                  }}
                />
              </ListItemIcon>
              <ListItemText>Edit room design</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleUploadClick}>
              <ListItemIcon>
                <Box
                  component="img"
                  src="/images/photo-light.svg"
                  alt="Upload"
                  sx={{
                    width: 18,
                    height: 18,
                    filter: theme.palette.mode === 'dark'
                      ? 'brightness(0) invert(1)'
                      : 'none'
                  }}
                />
              </ListItemIcon>
              <ListItemText>Upload room image</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDuplicateClick}>
              <ListItemIcon>
                <Box
                  component="img"
                  src="/images/copy-light.svg"
                  alt="Duplicate"
                  sx={{
                    width: 18,
                    height: 18,
                    filter: theme.palette.mode === 'dark'
                      ? 'brightness(0) invert(1)'
                      : 'none'
                  }}
                />
              </ListItemIcon>
              <ListItemText>Duplicate</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleExportClick}>
              <ListItemIcon>
                <Box
                  component="img"
                  src="/images/Export.svg"
                  alt="Export"
                  sx={{
                    width: 18,
                    height: 18,
                    filter: theme.palette.mode === 'dark'
                      ? 'brightness(0) invert(1)'
                      : 'none'
                  }}
                />
              </ListItemIcon>
              <ListItemText>Export</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleDeleteClick}>
              <ListItemIcon>
                <DeleteIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={<span style={{ color: theme.palette.error.main }}>Delete</span>}
              />
            </MenuItem>
          </Menu>

          {/* Duplicate Dialog */}
          <DesignNameDialog
            open={duplicateDialogOpen}
            onClose={() => setDuplicateDialogOpen(false)}
            onConfirm={handleDuplicateConfirm}
            mode="duplicate"
            initialName={selectedDesignId ? `${designs.find(d => d.id === selectedDesignId)?.project || 'Untitled Design'} - Copy` : 'Untitled Design - Copy'}
            designType="Conference Room"
            showWorkplaceLocation={true}
            organizationId={organizationId}
            initialLocationId={selectedDesignId ? designs.find(d => d.id === selectedDesignId)?.locationId || '' : ''}
          />

          {/* Export Dialog */}
          <ExportDialog
            open={exportDialogOpen}
            onClose={() => setExportDialogOpen(false)}
            onExportWorkplace={handleExportWorkplace}
            onExportPDF={handleExportPDF}
          />
        </>
      )}
    </ThumbnailContainer>
  );
};

// Styled Components
const ThumbnailContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 200px))',
  gap: '16px',
  maxHeight: 'calc(100vh - 260px)',
  overflow: 'auto',
  justifyContent: 'start',
  padding: '4px',
  backgroundColor: theme.palette.background.default,
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.default,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.action.disabled,
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.action.selected,
    },
  },
}));

const ThumbnailCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: '12px',
  borderRadius: (typeof theme.shape.borderRadius === 'number' ? theme.shape.borderRadius * 2 : theme.shape.borderRadius),
  boxShadow: theme.shadows[1],
  backgroundColor: theme.palette.background.paper,
  transition: 'all 0.2s ease-in-out',
  width: '100%',
  maxWidth: '200px',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
    borderColor: theme.palette.primary.main,
  },
}));

const ThumbnailHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundImage',
})<{ backgroundImage: string }>(({ backgroundImage, theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  padding: '8px',
  marginBottom: '8px',
  width: '100%',
  height: '100px',
  borderRadius: theme.shape.borderRadius,
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundColor: theme.palette.grey[200],
  border: `1px solid ${theme.palette.divider}`,
}));

const ThumbnailDetails = styled(Box)({
  width: '100%',
  textAlign: 'left',
  '& > *': {
    marginBottom: '2px',
  },
  '& > *:last-child': {
    marginBottom: 0,
  },
});

const HamburgerMenuButton = styled(Box)({
  position: 'absolute',
  bottom: '4px',
  right: '4px',
  zIndex: 2,
});

const SelectAllContainer = styled(Box)(({ theme }) => ({
  gridColumn: '1 / -1', // Span across all columns
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0',
  marginBottom: '0',
}));

export default DesignsThumbnail;